import { LitElement, PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';

import { trackImpression } from '../analytics/events/impression';
import { CookieConsent, Widget } from '../enums';
import { InvalidArgumentError } from '../utils/errors';
import {
  FeedAnimation,
  PATH_CONFIG_KEY,
} from './pimster-feed-widget.constants';
import { EMBED_SERVER_URL } from '../constants';
import { renderContent } from './pimster-feed-widget.render';
import pimsterFeedWidgetConfig from './pimster-feed-widget.config';
import { pimsterFeedWidgetStyle } from './pimster-feed-widget.style';
import {
  InternalMessageType,
  isInternalMessageEvent,
} from '../lib/internal-messages';
import { booleanConverter, booleanOrStringConverter } from '../utils/converter';
import { StoryPreviewDisplay } from '../pimster-story-preview/pimster-story-preview.constants';

const { name } = pimsterFeedWidgetConfig;

@customElement(name)
class PimsterFeedWidget extends LitElement {
  @property({ type: Array<FeedAnimation> })
  animations?: FeedAnimation[] = undefined;

  @property({ type: String })
  border?: string = undefined;

  @property({ type: String })
  borderColor?: string = undefined;

  @property({ type: Number })
  columns?: number = undefined;

  @property({ type: String })
  company?: string = undefined;

  @property({ type: `${CookieConsent}` })
  cookieConsent = CookieConsent.Ask;

  @property({ type: `${StoryPreviewDisplay}` })
  display?: StoryPreviewDisplay = undefined;

  @property({ type: String })
  font?: string = undefined;

  @property({ type: String })
  gap?: string = undefined;

  @property({ type: Number })
  moduleId?: number = undefined;

  @property({ type: String })
  product?: string = undefined;

  @property({ type: Boolean, converter: booleanConverter })
  withPlayIcon?: boolean = undefined;

  @property({ type: Boolean || String, converter: booleanOrStringConverter })
  withRadius?: boolean | string = undefined;

  @property({ type: Boolean, converter: booleanConverter })
  withStoryTitle?: boolean = undefined;

  @property({ type: String })
  private iframeId?: string = undefined;

  @query('iframe')
  iframe!: HTMLIFrameElement;

  @state()
  height: string = 'auto';

  private embedSrc: string = '';

  static styles = pimsterFeedWidgetStyle;

  private _buildRessourcesPath() {
    if (!this.company) {
      throw new InvalidArgumentError(
        'Element is missing at least one required argument'
      );
    }

    const path = `/company/${this.company}/product/${this.product || 'default'}/module/${this.moduleId}/feed`;
    const config = {
      animations: this.animations,
      border: this.border,
      borderColor: this.borderColor,
      columns: this.columns,
      cookieConsent: this.cookieConsent,
      display: this.display,
      font: this.font,
      gap: this.gap,
      withPlayIcon: this.withPlayIcon,
      withRadius: this.withRadius,
      withStoryTitle: this.withStoryTitle,
    };
    const configParam = encodeURIComponent(JSON.stringify(config));
    return `${path}?${PATH_CONFIG_KEY}=${configParam}`;
  }

  async connectedCallback() {
    super.connectedCallback();
    this._attachMessageListener();
    this.embedSrc = `${EMBED_SERVER_URL}${this._buildRessourcesPath()}`;
  }

  protected firstUpdated() {
    trackImpression({
      widgetType: Widget.Feed,
      company: this.company || '',
      moduleId: `${this.moduleId}`,
    });
  }

  protected update(changedProperties: PropertyValues): void {
    this.embedSrc = `${EMBED_SERVER_URL}${this._buildRessourcesPath()}`;
    super.update(changedProperties);
  }

  disconnectedCallback() {
    this._removeMessageListener();
    super.disconnectedCallback();
  }

  private _attachMessageListener() {
    window.addEventListener('message', this._messageHandler.bind(this));
  }

  private _removeMessageListener() {
    window.removeEventListener('message', this._messageHandler.bind(this));
  }

  private _messageHandler(message: MessageEvent) {
    if (!isInternalMessageEvent(message)) return;
    const { type, payload } = message.data;

    switch (type) {
      case InternalMessageType.Resize:
        if (
          payload.filters.widgetId === String(this.moduleId) &&
          payload.filters.widgetType === Widget.Feed
        ) {
          this.height = payload.height;
        }

        break;

      default:
        break;
    }
  }

  render() {
    return renderContent({
      embedSrc: this.embedSrc,
      iframeId: this.iframeId,
      height: this.height,
    });
  }
}

export default PimsterFeedWidget;
